import { ProtocolCommand } from '../service-model/protocol-command.interface';
import { SHELL_PROTOCOL_OPERATIONS_NS } from './operations/shell-protocol-namespace.const';

// Optimal Order Arrangement (CR-20260830-002) shell operation + reply DTOs.
//
// The decomposition engine lives on the backend now (ETS.ShellService.Shell/OrderArrangement -
// a logical-bit-identical port of the owner's reference decompose_legs.py). This op is an ASYNC
// COMMAND: processCommand returns immediately and the reply arrives over the event stream as
// 'GetOptimalOrderArrangementReply', correlated by requestId - the GetAdjustmentSolutions shape,
// because the engine's wall-clock budgets can exceed the sync path's 120s WCF ceiling.
//
// Reply DTOs are camelCase to match the ESS wire casing (same as GetAdjustmentSolutionsReply).
// The backend's `kind` vocabulary is authoritative: condor, butterfly, iron-condor,
// iron-butterfly, box, strangle, straddle, vertical.

export interface OoaLegDto {
    /** 'Call' | 'Put'. */
    right: string;
    strike: number;
    /** Positive = buy, negative = sell. */
    signedQty: number;
}

/** One current-grouping ticket (one on-screen order card). */
export interface OoaTicketDto {
    legs: OoaLegDto[];
}

/**
 * One expiration's pile. `expiration` is an opaque courier label (echoed verbatim by the
 * backend, never parsed). `legs` is the NETTED pile the engine decomposes; `currentGrouping`
 * is the RAW on-screen grouping: the rule-A Unchanged verdict compares against it, AND (revision
 * 2, CH-5) it is the engine's input-packaging baseline - scored, reported vs the output, seeded
 * into the search when it is a clean catalog cover (the output can then never score worse).
 */
export interface OoaPileDto {
    expiration: string;
    legs: OoaLegDto[];
    currentGrouping: OoaTicketDto[];
}

export class GetOptimalOrderArrangement implements ProtocolCommand {
    constructor(
        public readonly requestId: string,
        public readonly piles: OoaPileDto[],
        /** The live underlying price; null leaves the backend's skew rank inert. */
        public readonly atm: number | null
    ) {
    }

    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.GetOptimalOrderArrangement`;
    }
}

/** One package = one order ticket. `legs` carry TOTAL signed quantities (mult already applied). */
export interface OoaPackageDto {
    kind: string;
    mult: number;
    balanced: boolean;
    /** 'debit'/'credit' for verticals, null otherwise. */
    forceTag: string | null;
    legs: OoaLegDto[];
}

/**
 * One delivered solution (or flagged torn variant): packages plus the naked residue (if degraded).
 * Solutions come in RANK order - solution one, then solution two when one qualifies (the screen
 * pages them '1 of 2' / '2 of 2', no label) - then torn variants (revision 2, CH-8).
 */
export interface OoaSolutionDto {
    packages: OoaPackageDto[];
    /** Mathematically unavoidable residue; composed into ONE ticket of its own (owner ruling). */
    naked: OoaLegDto[];
    /**
     * Joint-match engine: 'Offered' | 'BestShapes' | 'Cycle' (the retired two-team engine sent
     * 'Leanest' | 'BestShape' | 'Both'). NEVER READ by the frontend - the planner counts solutions.
     */
    team: string;
    /** True for a TORN VARIANT (multipliers tear the granularity g; shown only on a huge win). */
    torn: boolean;
    /** The torn variant's huge-win reason ('2 fewer packages' / '+56u balanced'); null otherwise. */
    tornReason: string | null;
}

/**
 * One pile's verdict. status: 'ClosesFully' | 'Degraded' | 'Unresolved' | 'Unchanged'.
 * Unchanged = EVERY returned solution's grouping is identical to the current grouping (rule A,
 * revision-2 all-winners form) - `solutions` is then empty. Degraded (owner ruling 2026-09-02): a
 * packaging that strands a leg is a failure and is never offered - `solutions` is empty and
 * `warnings` carries the reason ("no arrangement offered: ..." + the arithmetic); the pile keeps
 * its cards. (Before: a Degraded solution shipped its naked legs as a ticket and painted the run red.)
 */
export interface OoaPileResultDto {
    expiration: string;
    status: string;
    solutions: OoaSolutionDto[];
    warnings: string[];
}

export interface GetOptimalOrderArrangementReply {
    requestId: string;
    error: boolean;
    piles: OoaPileResultDto[];
}

/** The engine's truthful mid-search milestones (CR-20260901-003). */
export type OoaMilestone = 'searching' | 'arrangementFound' | 'noFullClosure' | 'partialFound';

/**
 * Mid-search progress push (CR-20260901-003), correlated by requestId like the reply. One event
 * per milestone TRANSITION: 'searching' = the full-closure search started, nothing in hand yet;
 * 'arrangementFound' = a complete arrangement is in hand and the search keeps improving it;
 * 'noFullClosure' = proven that no full grouping exists, the search moves to partial
 * arrangements; 'partialFound' = a partial (naked-remainder) arrangement is in hand.
 * `phaseBudgetSeconds` is the CURRENT phase's wall-clock ceiling (owner ruling 2026-09-01: the
 * user must always see the waiting-time cap, or a hang is indistinguishable from a long search).
 */
export interface GetOptimalOrderArrangementProgress {
    requestId: string;
    expiration: string;
    milestone: OoaMilestone;
    phaseBudgetSeconds: number;
}
